import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { Router } from "@angular/router";
import { Hotel } from "@models";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";

@Component({
  selector: "app-search-card",
  templateUrl: "./search-card.component.html",
  styleUrls: ["./search-card.component.scss"],
})
export class SearchCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() hotel: Hotel;
  @Input() canRequest: boolean;
  @Input() hotelsMapCopy: Hotel[];
  @Input() hotelsListCopy: Hotel[];
  @Input() firstSearch: boolean;
  @Input() mapHasBeenDragged: boolean;
  @Input() index: number;
  countStars: any[] = [];
  subscription: Subscription;
  translateSubscription: Subscription;
  isRestel = false;
  lang = "";
  @Input() searchConfig: any;
  @Input() filters: any;
  @Input() facilities: any;
  @Input() options: any;
  @Input() selectedUsers: any;
  @Input() selectedTravelers: any;
  @Input() projectData: any;
  @Input() originalSearchConfig: any;
  @Input() userFilters: any;
  @Input() totalNights: any;
  @Input() showHotels: any;
  @Input() selectedPolicy: any;
  @Input() filtersApplicated: any;
  currency: "€" | "$";
  expandableFilters = false;
  mapView = false;
  starAuthRequired = false;

  constructor(
    private router: Router,
    private translate: TranslateService,
  ) {
    this.currency = "€";
  }

  ngOnInit() {
    if (this.originalSearchConfig) {
      this.searchConfig = { ...this.originalSearchConfig };
    }

    this.lang = this.translate.currentLang;

    try {
      this.countStars = new Array(Math.floor(Number(this.hotel.stars)));
    } catch {
      this.countStars = [];
    }

    if (this.hotel?.code?.toString().startsWith("R")) {
      this.isRestel = true;
    }

    if (this.hotel?.images?.length === 0) {
      this.hotel.images.push("assets/img/gallery/image_not_available.png");
    }

    this.starAuthRequired = this.requiresAuthByStars(this.hotel);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['hotel'] || changes['userFilters']) {
      this.starAuthRequired = this.requiresAuthByStars(this.hotel);
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.translateSubscription) {
      this.translateSubscription.unsubscribe();
    }
  }

  changeSource(event, i) {
    this.hotel.images.splice(i, 1);
    this.hotel.images.length === 0
      ? (event.target.src = "assets/img/gallery/image_not_available.png")
      : null;
  }

  toggleFilter() {
    this.expandableFilters = !this.expandableFilters;
  }

  getHotel() {
    this.cleanHotelDetailLocalStorage();

    const stateKey = `hotel-detail-state-${crypto.randomUUID()}`;

    const stateData = {
      searchConfig: this.searchConfig,
      originalSearchConfig: this.originalSearchConfig,
      hotel: this.hotel,
      hotelsMapCopy: this.hotelsMapCopy?.length ? this.hotelsMapCopy : [this.hotel],
      hotelsListCopy: this.hotelsListCopy?.length ? this.hotelsListCopy : [this.hotel],
      filters: this.filters,
      facilities: this.facilities,
      selectedUsers: this.selectedUsers,
      selectedTravelers: this.selectedTravelers,
      projectData: this.projectData,
      starAuthRequired: this.starAuthRequired,
      userFilter: this.userFilters,
      selectedPolicy: this.selectedPolicy,
      filtersApplicated: this.filtersApplicated,
      createdAt: Date.now(),
    };

    localStorage.setItem(stateKey, JSON.stringify(stateData));

    const urlTree = this.router.createUrlTree(
      [`${this.router.url}/hotels`, this.hotel.code],
      {
        queryParams: {
          location: this.searchConfig.locationText,
          lat: this.searchConfig.latitude,
          lon: this.searchConfig.longitude,
          hotelUuid: this.hotel.uuid,
          checkin: `${this.searchConfig.checkIn.day}/${this.searchConfig.checkIn.month}/${this.searchConfig.checkIn.year}`,
          checkout: `${this.searchConfig.checkOut.day}/${this.searchConfig.checkOut.month}/${this.searchConfig.checkOut.year}`,
          canRequest: this.canRequest,
          stateKey,
        },
      }
    );

    const url = this.router.serializeUrl(urlTree);

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  isPackage() {
    return this.hotel.rooms.some((room) => room.rates.some((rate) => rate.saleScenario?.package === true))
  }

  requiresAuthByStars(hotel: any): boolean {
    const userStars  = Number(String(this.userFilters?.stars ?? '').replace(',', '.'));
    const raw        = hotel?.stars;

    const hotelStars = raw === 'S'
      ? 4
      : Number(String(raw ?? '').replace(',', '.'));

    if (Number.isNaN(userStars) || Number.isNaN(hotelStars)) return false;

    return userStars < Math.floor(hotelStars);
  }

  isCorporate() {
    return this.hotel.rooms.some((room) => room.rates.some((rate) => rate?.saleScenario?.corporate === true))
  }

  private cleanHotelDetailLocalStorage(): void {
    Object.keys(localStorage)
      .filter((key) => key.startsWith('hotel-detail-state-'))
      .forEach((key) => {
        try {
          const item = JSON.parse(localStorage.getItem(key) || '{}');
          const createdAt = Number(item.createdAt || 0);
          const maxAge = 1000 * 60 * 15;

          if (!createdAt || Date.now() - createdAt > maxAge) {
            localStorage.removeItem(key);
          }
        } catch {
          localStorage.removeItem(key);
        }
      });
  }

}
